import * as THREE from 'three';
import { TargetKind, type TargetKindId } from '../../config/targets';
import type { Rng } from '../../core/Rng';

/**
 * Procedural geometry for every target type (SPEC §5.1, §3.8.1).
 *
 * All eight archetypes are assembled from primitives and merged into one buffer geometry each, so
 * a target is a single draw call. No external assets exist anywhere in this project (SPEC §5), and
 * these are the objects the player spends the last four seconds of a run looking at — so each one
 * needs a recognisable silhouette from a nose camera at 300 m, and nothing more.
 *
 * Triangle budgets follow §5.1: vehicles 300–800, ships ~1500, buildings 100–400. The shapes are
 * chosen for *identifiability under haze and compression* rather than for detail, because by the
 * time the player can resolve detail the run is already committed.
 *
 * Local origin sits at the target's base, +y up, the long axis along +x. Metres throughout.
 */

/** Merge a list of positioned primitives into one geometry. */
function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let vertexCount = 0;
  let indexCount = 0;
  for (const part of parts) {
    vertexCount += part.attributes['position'].count;
    const index = part.getIndex();
    indexCount += index === null ? part.attributes['position'].count : index.count;
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const indices = new Uint16Array(indexCount);

  let vertexOffset = 0;
  let indexOffset = 0;
  for (const part of parts) {
    const partPositions = part.attributes['position'].array as ArrayLike<number>;
    const partNormals = part.attributes['normal'].array as ArrayLike<number>;
    const count = part.attributes['position'].count;
    positions.set(partPositions, vertexOffset * 3);
    normals.set(partNormals, vertexOffset * 3);

    const index = part.getIndex();
    if (index === null) {
      for (let i = 0; i < count; i += 1) indices[indexOffset + i] = vertexOffset + i;
      indexOffset += count;
    } else {
      for (let i = 0; i < index.count; i += 1) {
        indices[indexOffset + i] = vertexOffset + index.getX(i);
      }
      indexOffset += index.count;
    }
    vertexOffset += count;
    part.dispose();
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeBoundingSphere();
  return geometry;
}

/** A box placed by centre and size. */
function box(
  x: number,
  y: number,
  z: number,
  width: number,
  height: number,
  depth: number,
): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

/** A cylinder standing on its base, or laid along an axis when rotated. */
function cylinder(
  x: number,
  y: number,
  z: number,
  radius: number,
  height: number,
  segments = 10,
): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, segments);
  geometry.translate(x, y + height * 0.5, z);
  return geometry;
}

/**
 * Radar antenna: a rotating dish on a short mast and a cabin.
 * Fragile and small — the difficulty is putting the aircraft on it, not the warhead.
 */
function radarAntenna(): THREE.BufferGeometry {
  const dish = new THREE.SphereGeometry(2.6, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.42);
  dish.rotateX(-Math.PI * 0.32);
  dish.translate(0, 5.4, 0);
  return merge([
    box(0, 0.9, 0, 4.2, 1.8, 2.6), // cabin
    cylinder(0, 1.8, 0, 0.28, 2.6, 8), // mast
    dish,
  ]);
}

/** SHORAD launcher: a tracked hull with a turret and four raised launch tubes. */
function samLauncher(): THREE.BufferGeometry {
  const parts = [
    box(0, 0.9, 0, 6.4, 1.4, 2.9), // hull
    box(0, 0.5, 1.55, 6.2, 0.9, 0.5), // track
    box(0, 0.5, -1.55, 6.2, 0.9, 0.5),
    box(-0.4, 2.1, 0, 2.6, 1.1, 2.2), // turret
  ];
  // Four tubes, canted up off the rail.
  for (let i = 0; i < 4; i += 1) {
    const tube = new THREE.CylinderGeometry(0.19, 0.19, 3.1, 8);
    tube.rotateZ(Math.PI * 0.5);
    tube.rotateZ(-0.32);
    tube.translate(0.5, 2.9, -0.75 + i * 0.5);
    parts.push(tube);
  }
  return merge(parts);
}

/** Fuel storage: a squat cylinder farm. Large, forgiving, and worth the secondary. */
function fuelStorage(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const positions: readonly (readonly [number, number])[] = [
    [0, 0],
    [9, 2],
    [-2, 9],
  ];
  for (const [x, z] of positions) {
    parts.push(cylinder(x, 0, z, 4.2, 7.5, 14));
    // Roof rim, so the silhouette reads as a tank rather than a drum.
    const rim = new THREE.TorusGeometry(4.25, 0.2, 4, 14);
    rim.rotateX(Math.PI * 0.5);
    rim.translate(x, 7.5, z);
    parts.push(rim);
  }
  parts.push(box(3.5, 0.4, 5, 14, 0.8, 0.6)); // pipe run
  return merge(parts);
}

/** Ammunition bunker: a low earth-covered mound with a steel door. The hard setup. */
function ammoBunker(): THREE.BufferGeometry {
  const mound = new THREE.CylinderGeometry(0, 7.5, 3.4, 14, 1);
  mound.scale(1, 1, 0.7);
  mound.translate(0, 1.7, 0);
  return merge([
    box(0, 1.5, 0, 12, 3, 8), // buried body
    mound,
    box(6.2, 1.4, 0, 0.6, 2.8, 3.2), // blast door
  ]);
}

/** Command post: a hardened two-storey block with a comms fit on the roof. */
function commandPost(): THREE.BufferGeometry {
  const parts = [
    box(0, 3, 0, 12, 6, 9), // main block
    box(0, 6.4, 0, 12.6, 0.8, 9.6), // roof slab
    box(-7, 1.6, 0, 2.4, 3.2, 4), // entrance
    cylinder(4, 6.8, 3, 0.16, 4, 6), // whip antenna
  ];
  const dish = new THREE.SphereGeometry(1.1, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.45);
  dish.rotateX(-Math.PI * 0.35);
  dish.translate(-3, 7.8, -2);
  parts.push(dish);
  return merge(parts);
}

/** Naval vessel: a lofted hull, deckhouse and mast. Around 1500 triangles per §5.1. */
function navalVessel(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // Hull as a stack of tapering boxes — cheap, and the sheer reads correctly from the air.
  const sections = 9;
  for (let i = 0; i < sections; i += 1) {
    const t = i / (sections - 1);
    const x = -26 + t * 52;
    // Fine at the bow, full amidships, tucked at the stern.
    const beam = 7.4 * Math.sin(Math.PI * Math.min(1, 0.15 + t * 0.85)) + 1.2;
    parts.push(box(x, 1.6, 0, 52 / sections + 0.2, 3.2, beam));
  }
  parts.push(box(-2, 4.6, 0, 20, 3.6, 7)); // deckhouse
  parts.push(box(-4, 7.4, 0, 9, 2.2, 5.2)); // bridge
  parts.push(cylinder(-4, 8.5, 0, 0.22, 7, 6)); // mast
  parts.push(box(14, 4.2, 0, 3.2, 1.6, 2.6)); // forward mount
  parts.push(box(-18, 4.4, 0, 4.4, 1.8, 3.4)); // hangar
  return merge(parts);
}

/** Bridge span: deck, piers and side trusses. The precision showcase. */
function bridgeSpan(): THREE.BufferGeometry {
  const parts = [
    box(0, 6, 0, 44, 0.9, 9), // deck
    box(0, 6.9, 4.3, 44, 1.1, 0.4), // parapets
    box(0, 6.9, -4.3, 44, 1.1, 0.4),
    box(-15, 3, 0, 2.6, 6, 6), // piers
    box(15, 3, 0, 2.6, 6, 6),
  ];
  // Truss diagonals: what makes it read as a bridge rather than a wall.
  for (let i = 0; i < 8; i += 1) {
    const x = -19 + i * 5.4;
    const diagonal = new THREE.BoxGeometry(6.4, 0.35, 0.35);
    diagonal.rotateZ(i % 2 === 0 ? 0.62 : -0.62);
    diagonal.translate(x, 8.2, 4.3);
    parts.push(diagonal);
  }
  return merge(parts);
}

/** Comms mast: a guyed lattice. Thin — genuinely difficult, exactly as the catalogue says. */
function commsMast(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [
    box(0, 0.6, 0, 3.4, 1.2, 3.4), // base pad
    cylinder(0, 1.2, 0, 0.3, 26, 6),
  ];
  // Cross bracing at intervals, and three guys.
  for (let i = 0; i < 5; i += 1) {
    const y = 5 + i * 5;
    const arm = new THREE.BoxGeometry(2.6, 0.16, 0.16);
    arm.translate(0, y, 0);
    parts.push(arm);
  }
  for (let i = 0; i < 3; i += 1) {
    const angle = (i / 3) * Math.PI * 2;
    const guy = new THREE.BoxGeometry(0.1, 22, 0.1);
    guy.rotateZ(0.42);
    guy.rotateY(angle);
    guy.translate(Math.cos(angle) * 4.5, 11, Math.sin(angle) * 4.5);
    parts.push(guy);
  }
  return merge(parts);
}

const BUILDERS: Record<TargetKindId, () => THREE.BufferGeometry> = {
  [TargetKind.RadarAntenna]: radarAntenna,
  [TargetKind.SamLauncher]: samLauncher,
  [TargetKind.FuelStorage]: fuelStorage,
  [TargetKind.AmmoBunker]: ammoBunker,
  [TargetKind.CommandPost]: commandPost,
  [TargetKind.NavalVessel]: navalVessel,
  [TargetKind.BridgeSpan]: bridgeSpan,
  [TargetKind.CommsMast]: commsMast,
};

/**
 * Build the geometry for a target type. One geometry per *type*, shared by every instance of it —
 * these are static objects and there are never many, so instancing would cost more than it saves.
 */
export function createTargetGeometry(kind: TargetKindId): THREE.BufferGeometry {
  return BUILDERS[kind]();
}

/** Base colour per type. Weathered military palette; the grade in the post stack does the rest. */
export function targetColour(kind: TargetKindId, rng: Rng): THREE.Color {
  const base: Record<TargetKindId, number> = {
    [TargetKind.RadarAntenna]: 0x8d9186,
    [TargetKind.SamLauncher]: 0x5c6350,
    [TargetKind.FuelStorage]: 0x9aa0a4,
    [TargetKind.AmmoBunker]: 0x6f6f5e,
    [TargetKind.CommandPost]: 0x8a8779,
    [TargetKind.NavalVessel]: 0x76808a,
    [TargetKind.BridgeSpan]: 0x9c9c98,
    [TargetKind.CommsMast]: 0xa8a49a,
  };
  const colour = new THREE.Color(base[kind]);
  // Per-instance variation, seeded — the same theatre paints the same vehicles every run.
  const shade = 0.88 + rng.next() * 0.24;
  return colour.multiplyScalar(shade);
}
